import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import * as Lucide from 'lucide-react';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo, { faqSchema, breadcrumbSchema } from '../components/Seo';
import { SERVICES } from '../mock';

const faq = (title) => [
  { q: `Do I need a referral to start ${title}?`, a: 'No referral is needed. You can reach out directly to schedule an initial consultation.' },
  { q: 'How long are sessions?', a: 'Sessions are typically 50 minutes. Your therapist may recommend a rhythm that works best for your goals.' },
  { q: 'Do you offer telehealth?', a: 'Yes. We offer secure video sessions throughout Virginia in addition to in-person appointments in Chesapeake, VA.' },
  { q: 'What if I’m not sure this is right for me?', a: 'That’s completely okay. Reach out and we’ll help you figure out the next step — no pressure.' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-soft p-5">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center text-left">
        <span className="font-serif-display text-lg text-[color:var(--deep-sage)]">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[color:var(--sage-dark)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="mt-3 text-sm text-[color:var(--taupe-gray)] leading-relaxed">{a}</p>}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find(s => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = Lucide[service.icon] || Lucide.Heart;
  const faqs = faq(service.title);

  return (
    <>
      <Seo
        title={service.seoTitle}
        description={service.seoDesc}
        path={`/services/${service.slug}`}
        schema={faqSchema(faqs)}
      />
      <PageHero
        eyebrow="Service"
        title={service.title}
        highlight="in Chesapeake, VA"
        subtitle={service.short}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Services', to: '/services' }, { label: service.title }]}
      />

      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl serviceCard-icon flex items-center justify-center">
              <Icon className="w-6 h-6 text-[color:var(--dusty-rose)]" strokeWidth={1.4} />
            </div>
            <div className="font-script text-3xl text-[color:var(--dusty-rose)]">A gentle path forward</div>
          </div>

          {service.intro && (
            <p className="text-lg text-[color:var(--deep-sage)] leading-relaxed mb-10 reveal">{service.intro}</p>
          )}

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="card-soft p-7 reveal">
              <div className="eyebrow mb-3">What You May Be Experiencing</div>
              <h3 className="font-serif-display text-2xl mb-4 text-[color:var(--deep-sage)]">You are not alone in this.</h3>
              <ul className="space-y-3">
                {service.experiencing.map(e => (
                  <li key={e} className="flex gap-2 items-start text-[color:var(--taupe-gray)]">
                    <Check className="w-4 h-4 mt-1 text-[color:var(--dusty-rose)] flex-shrink-0" /> <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-soft p-7 reveal">
              <div className="eyebrow mb-3">Our Approach</div>
              <h3 className="font-serif-display text-2xl mb-4 text-[color:var(--deep-sage)]">How we help</h3>
              <p className="text-[color:var(--taupe-gray)] leading-relaxed mb-4">{service.approach}</p>
              <p className="text-[color:var(--deep-sage)] italic">Your care is personalized — there is no one-size-fits-all path to healing.</p>
            </div>
          </div>

          {service.modalities && (
            <div className="mb-12">
              <div className="text-center mb-8">
                <div className="eyebrow mb-3">How We Can Help</div>
                <h3 className="font-serif-display text-3xl text-[color:var(--deep-sage)]">Modalities We Use</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {service.modalities.map((m, i) => (
                  <div key={m.title} className="card-soft p-6 reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                    <h4 className="font-serif-display text-lg text-[color:var(--dusty-rose)] mb-2">{m.title}</h4>
                    <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed">{m.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {service.outcomes && (
            <div className="card-soft p-8 md:p-10 mb-12 reveal" style={{ background: 'linear-gradient(135deg, rgba(232,165,169,0.08), rgba(184,190,164,0.12))' }}>
              <div className="eyebrow mb-3">Your Transformation</div>
              <h3 className="font-serif-display text-3xl mb-6 text-[color:var(--deep-sage)]">Through therapy, you can begin to:</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {service.outcomes.map(o => (
                  <li key={o} className="flex gap-2 items-start text-[color:var(--deep-sage)]">
                    <Check className="w-4 h-4 mt-1 text-[color:var(--dusty-rose)] flex-shrink-0" /> <span>{o}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 font-script text-2xl text-[color:var(--dusty-rose)]">Healing is possible, and you don’t have to do it alone.</p>
            </div>
          )}

          <div className="mb-12">
            <div className="eyebrow mb-3">FAQ</div>
            <h3 className="font-serif-display text-2xl mb-6 text-[color:var(--deep-sage)]">Frequently Asked Questions</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {faqs.map(f => <FAQItem key={f.q} {...f} />)}
            </div>
          </div>

          <div>
            <h3 className="font-serif-display text-2xl mb-6 text-[color:var(--deep-sage)]">Related Services</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {service.related.map(r => {
                const rs = SERVICES.find(s => s.slug === r);
                if (!rs) return null;
                return (
                  <Link key={r} to={`/services/${r}`} className="card-soft p-5 group">
                    <div className="font-serif-display text-lg text-[color:var(--deep-sage)]">{rs.title}</div>
                    <div className="mt-2 inline-flex items-center gap-1 text-sm text-[color:var(--dusty-rose)]">Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
